/**
 * The ordered create-pool steps, the title of each, and the "Step N of M"
 * label.
 *
 * Five steps match frame 21: league, setup (Quick start or set it up
 * myself), pool basics, Survivor rules, then review. The setup step is
 * skipped when it has no Quick start to offer (ISSUE-1093).
 */

import {
  isSetupSlotPoolType,
  POOL_TYPE_SETUP_STEP_TITLES,
} from "./poolTypeSetups";

export type typeCreatePoolStepId =
  "league" | "setup" | "basics" | "rules" | "review";

export const CREATE_POOL_STEP_IDS: readonly typeCreatePoolStepId[] = [
  "league",
  "setup",
  "basics",
  "rules",
  "review",
];

export const CREATE_POOL_STEP_TITLES: Record<typeCreatePoolStepId, string> = {
  league: "League & contest",
  setup: "How do you want to set this up?",
  basics: "Pool basics",
  rules: "Survivor rules",
  review: "Review the rules",
};

export const CREATE_POOL_FIRST_STEP: typeCreatePoolStepId = "league";

/**
 * ISSUE-1258. The rules step is Survivor's rules for a Survivor pool and the
 * type's own setup step for every other type (`poolTypeSetups.ts`).
 */
export function createPoolStepTitle(
  stepId: typeCreatePoolStepId,
  contestType: string | null,
): string {
  if (stepId === "rules" && isSetupSlotPoolType(contestType)) {
    return POOL_TYPE_SETUP_STEP_TITLES[contestType];
  }
  return CREATE_POOL_STEP_TITLES[stepId];
}

/**
 * ISSUE-1093. The steps a fan actually walks. The setup step only offers a
 * choice when the league has a Quick start; with none it would be one
 * preselected option, so it is skipped. Counter, Next and Back all read this
 * one list, so they agree.
 */
export function visibleCreatePoolSteps(input: {
  isSetupSkipped: boolean;
}): readonly typeCreatePoolStepId[] {
  return input.isSetupSkipped
    ? CREATE_POOL_STEP_IDS.filter((stepId) => stepId !== "setup")
    : CREATE_POOL_STEP_IDS;
}

export function createPoolStepIndex(
  stepId: typeCreatePoolStepId,
  steps: readonly typeCreatePoolStepId[] = CREATE_POOL_STEP_IDS,
): number {
  return steps.indexOf(stepId);
}

export function nextCreatePoolStep(
  stepId: typeCreatePoolStepId,
  steps: readonly typeCreatePoolStepId[] = CREATE_POOL_STEP_IDS,
): typeCreatePoolStepId | null {
  const index = createPoolStepIndex(stepId, steps);
  return steps[index + 1] ?? null;
}

export function previousCreatePoolStep(
  stepId: typeCreatePoolStepId,
  steps: readonly typeCreatePoolStepId[] = CREATE_POOL_STEP_IDS,
): typeCreatePoolStepId | null {
  const index = createPoolStepIndex(stepId, steps);
  return index <= 0 ? null : (steps[index - 1] ?? null);
}

/**
 * "Step 2 of 4". ISSUE-1303: until the fan has picked a league and a contest
 * type and the Quick start read has answered, nobody knows yet whether the
 * setup step is skipped, so the label leaves the total off ("Step 1") rather
 * than show a five that turns into a four.
 */
export function createPoolStepCounterLabel(
  stepId: typeCreatePoolStepId,
  steps: readonly typeCreatePoolStepId[] = CREATE_POOL_STEP_IDS,
  isTotalKnown = true,
): string {
  const position = `Step ${createPoolStepIndex(stepId, steps) + 1}`;
  return isTotalKnown ? `${position} of ${steps.length}` : position;
}

export function isLastCreatePoolStep(
  stepId: typeCreatePoolStepId,
  steps: readonly typeCreatePoolStepId[] = CREATE_POOL_STEP_IDS,
): boolean {
  return createPoolStepIndex(stepId, steps) === steps.length - 1;
}
